<template>
  <b-overlay :show="loading">
    <b-alert
      variant="danger"
      :show="consultationData === undefined && !loading"
    >
      <h4 class="alert-heading">
        Error fetching Consultation Data
      </h4>

      <div class="alert-body">
        <b-link
          class="alert-link"
          :to="{ name: 'admin-consultations'}"
        >
          Consultations
        </b-link>
      </div>
    </b-alert>

    <template v-if="consultationData && patientData && doctorData">
      <div>
        <b-row class="d-flex mt-0 justify-content-end">
          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing' && can('consultations:CanCancel')"
              block
              type="submit"
              class="mb-5 mx-2 cancel-btn"
              variant="outline-danger" 
              style="width: 200px"
              :disabled="loading"
              @click="onCancel"
            >
              <feather-icon icon="XIcon" />
              <span class="align-middle ml-50">Cancel</span>
            </b-button>
          </b-col>

          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing' && can('consultations:CanUpdate')"
              block
              type="primary"
              class="mb-5 mx-2"
              variant="outline-primary" 
              style="width: 200px"
              :disabled="loading"
              @click="onViewEdit(consultationData.id)"
            >
              <feather-icon icon="PenToolIcon" />
              <span class="align-middle ml-50">Edit</span>
            </b-button>
          </b-col>

          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing' && can('consultations:CanMarkAsCompleted')"
              variant="primary"
              block
              type="submit"
              class="mb-5 mx-2"
              style="width: 220px"
              :disabled="loading"
              @click="onComplete"
            >
              <feather-icon icon="CheckIcon" />
              <span class="align-middle ml-50">Mark as completed</span>
            </b-button>
          </b-col>
          
          <b-col cols="auto">
            <b-button
              v-if="consultationData.status === 'ongoing'"
              variant="outline-primary"
              block
              type="submit"
              class="mb-5 mx-2"
              style="width: 220px"
              :disabled="loading"
              @click="showMedicalHistory"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">Medical History</span>
            </b-button>
          </b-col>

          <b-col cols="auto">
            <b-button
              v-if="(consultationData.status === 'cancelled' || consultationData.status === 'completed') && can('consultations:CanReopen')"
              block
              type="submit"
              class="mb-5 mx-2"
              variant="info" 
              style="width: 220px"
              :disabled="loading"
              @click="onReopen"
            >
              <feather-icon icon="Edit2Icon" />
              <span class="align-middle ml-50">Re-open consultation</span>
            </b-button>
          </b-col>
        </b-row>

        <b-row class="match-height">
          <b-col cols="12" md="12">
            <b-row class="d-flex details-wrapper align-items-center">
              <b-col cols="12" md="4">
                <p>
                  Patient Name: {{ patientData.full_name }}
                </p>
              </b-col>
              <b-col cols="12" md="4">
                <p>
                  Insurance Number: {{ patientData.policy_number }}
                </p>
              </b-col>
              <b-col cols="12" md="4">
                <p>
                  Attending Physician: {{ doctorData.full_name }}
                </p>
              </b-col>
            </b-row>

            <b-row class="match-height mt-2">
              <b-col cols="12" md="4">
                <dietician-advice-status
                  :consultation-data="consultationData" 
                />
              </b-col>

              <b-col cols="12" md="4">
                <dietician-uploads-status
                  :consultation-data="consultationData" 
                />
              </b-col>

              <b-col cols="12" md="4">
                <dieticians-note-status
                  :consultation-data="consultationData" 
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <medical-history-sidebar
          :patient-data="patientData"
          :is-medical-history-sidebar-active.sync="isMedicalHistorySidebarActive"
        />
      </div>
    </template>
  </b-overlay>
</template>

<script>
import { get } from "lodash";
import { BAlert, BAvatar, BBadge, BImg, BCard, BCol, BLink, BOverlay, BRow, BCardImg, BButton } from "bootstrap-vue"

import VueEasyLightbox from 'vue-easy-lightbox';
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import DieticiansNoteStatus from '@core/components/shared/admin/consultation/DieticiansNoteStatus.vue';
import DieticianAdviceStatus from '@core/components/shared/admin/consultation/DieticianAdviceStatus.vue';
import DieticianUploadsStatus from '@core/components/shared/admin/consultation/DieticianUploadsStatus.vue';
import MedicalHistorySidebar from "@core/components/shared/admin/consultation/MedicalHistorySidebar.vue"


export default {
  components: {
    ListTable,
    VueEasyLightbox,
    BCardImg,
    BOverlay,
    BAlert,
    BAvatar,
    BLink,
    BImg,
    BBadge,
    BRow,
    BCol,
    BCard,
    BButton,
    // components
    DieticiansNoteStatus,
    DieticianAdviceStatus,
    DieticianUploadsStatus,
    MedicalHistorySidebar,
  },
  directives: {},
  data(){
    return {
      loading: false,

      doctorData: null,
      patientData: null,
      consultationData: null,
      isMedicalHistorySidebarActive: false,
    }
  },
  created() {
    this.fetchConsultationData();
  },
  methods: {
    async fetchConsultationData(){
      try {
        this.loading = true;
        const { id } = this.$route.params;

        const response = await this.useJwt().mainService.consultations.fetch(id);
        this.consultationData = get(response, 'data.data');

        await this.fetchPatient()
        await this.fetchDoctor()
      } catch (error){
        this.consultationData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchPatient(){
      try {
        this.loading = true;
        const { patient_id } = this.consultationData

        const response = await this.useJwt().mainService.patients.fetch(patient_id);
        this.patientData = get(response, 'data.data')
      } catch (error){
        this.patientData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            variant: 'danger',
            text: error_message,
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchDoctor(){
      try {
        this.loading = true;
        const { created_by } = this.consultationData

        const doctor_response = await this.useJwt().mainService.users.fetch(created_by)
        this.doctorData = get(doctor_response, 'data.data');
      } catch (error){
        this.consultationData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onCancel(){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can(`consultations:CanUpdate`)) {
          return
        }

        await this.useJwt().mainService.consultations.cancel(this.consultationData.id);
        this.fetchConsultationData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Consultation cancelled successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onReopen(){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can(`consultations:CanUpdate`)) {
          return
        }

        await this.useJwt().mainService.consultations.reopen(this.consultationData.id);
        this.fetchConsultationData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Consultation reopened successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onComplete(){
      try {
        this.loading = true;
        this.errorMessage = "";

        if (!this.can(`consultations:CanMarkAsCompleted`)) {
          return
        }

        await this.useJwt().mainService.consultations.complete(this.consultationData.id);
        this.fetchConsultationData()

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Consultation marked as completed successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    onRefreshRef(ref){
      if (this.$refs[ref]){
        this.$refs[ref].fetchAllData();
      }
    },
    onViewEdit(consultation_id){
      this.$router.push({
        name: 'admin-dietary-consultation-edit',
        params: { id: consultation_id }
      })
    },
    showMedicalHistory(){
      this.isMedicalHistorySidebarActive = !this.isMedicalHistorySidebarActive;
    }
  }
}
</script>

<style lang="scss">
  small {
    font-size: 10px;
  }

  .cancel-btn {
    margin-top: 8px !important;
  }

  .details-wrapper {
    background-color: #EAF1F9;
    padding: 22px 4px 12px 20px;
    margin-left: 3px;
    margin-right: 3px;
  }

  .details-wrapper div p {
    font-size: 16px
  }
</style>
